import { Stethoscope, Sparkles } from "lucide-react";
import { maskMobile } from "@/lib/membership";
import { LOGO_DATA_URL } from "@/lib/logoData";

// Stylised single-side membership card. Used in hero preview & generation success.
// Height is content-driven (no fixed aspect ratio) so nothing ever clips — the
// card grows to fit its content on every device/browser. Fonts use fixed
// responsive sizes (no container queries) for universal compatibility.
export const MembershipCardPreview = ({
  name = "Patient Name",
  mobile = "9999999999",
  membershipNumber = "XXXXXXXX",
  issued = "",
  expires = "",
  variant = "showcase", // showcase | flat
  innerRef = null,
}) => {
  return (
    <div
      ref={innerRef}
      className={
        "group relative w-full max-w-[420px] mx-auto rounded-[22px] overflow-hidden bg-gradient-card text-primary-foreground card-shine " +
        (variant === "showcase" ? "shadow-card-3d ring-aqua-glow" : "shadow-elegant")
      }
    >
      {/* Decorative noise + waves */}
      <div className="noise-overlay" />
      <svg
        viewBox="0 0 600 380"
        className="absolute inset-0 w-full h-full opacity-[0.22]"
        preserveAspectRatio="xMidYMid slice"
        aria-hidden="true"
      >
        <defs>
          <linearGradient id="wave" x1="0" x2="1" y1="0" y2="1">
            <stop offset="0%" stopColor="#fff" stopOpacity="0.8" />
            <stop offset="100%" stopColor="#fff" stopOpacity="0" />
          </linearGradient>
        </defs>
        <path d="M0 260 C 120 220 200 320 320 270 C 440 220 520 300 600 260 L 600 380 L 0 380 Z" fill="url(#wave)" />
        <path d="M0 300 C 120 270 220 340 340 300 C 460 260 520 330 600 310 L 600 380 L 0 380 Z" fill="url(#wave)" opacity="0.55" />
      </svg>

      <div className="relative flex flex-col gap-3.5 sm:gap-5 p-4 sm:p-6">
        {/* Top row: logo + brand */}
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-2 sm:gap-2.5 min-w-0">
            <span className="inline-flex items-center rounded-lg bg-white px-1.5 py-1 sm:px-2 sm:py-1.5 shadow-sm shrink-0">
              <img src={LOGO_DATA_URL} alt="1Rupee Clinic" className="h-6 sm:h-7 w-auto object-contain" />
            </span>
            <div className="leading-tight min-w-0">
              <div className="text-[9px] sm:text-[10px] uppercase tracking-[0.18em] sm:tracking-[0.22em] text-primary-foreground/80">
                Annual Membership
              </div>
            </div>
          </div>
          <div className="text-right shrink-0">
            <div className="inline-flex items-center gap-1 sm:gap-1.5 rounded-full bg-primary-foreground/15 ring-1 ring-primary-foreground/25 backdrop-blur px-2 py-1 text-[9px] sm:text-[10px] font-semibold uppercase tracking-wider">
              <Sparkles className="h-2.5 w-2.5 sm:h-3 sm:w-3" />
              Member
            </div>
          </div>
        </div>

        {/* Middle: name + perk */}
        <div className="min-w-0">
          <div className="text-[9px] sm:text-[10px] uppercase tracking-[0.18em] sm:tracking-[0.22em] text-primary-foreground/70">
            Cardholder
          </div>
          <div className="font-display font-semibold text-xl sm:text-[26px] leading-tight truncate">
            {name || "—"}
          </div>
          <div className="mt-1.5 inline-flex items-center gap-1.5 sm:gap-2 text-[10px] sm:text-xs rounded-md bg-primary-foreground/12 ring-1 ring-primary-foreground/20 px-2 py-1 text-primary-foreground/95">
            <Stethoscope className="h-3 w-3 sm:h-3.5 sm:w-3.5 shrink-0" />
            <span>Free consults · 10% off diagnostics</span>
          </div>
        </div>

        {/* Bottom row */}
        <div className="flex items-end justify-between gap-3">
          <div className="min-w-0">
            <div className="text-[9px] uppercase tracking-[0.18em] sm:tracking-[0.22em] text-primary-foreground/70">
              Membership No.
            </div>
            <div className="font-mono-card font-semibold text-sm sm:text-[15px] tracking-wider truncate">
              {membershipNumber}
            </div>
            <div className="mt-1 text-[10px] text-primary-foreground/80 truncate">
              {maskMobile(mobile)}
            </div>
          </div>
          <div className="text-right shrink-0">
            <div className="text-[9px] uppercase tracking-[0.18em] sm:tracking-[0.22em] text-primary-foreground/70">
              Valid till
            </div>
            <div className="font-mono-card text-[13px] sm:text-sm font-semibold whitespace-nowrap">
              {expires || "—"}
            </div>
            {issued && (
              <div className="text-[10px] text-primary-foreground/70 mt-0.5 whitespace-nowrap">Issued {issued}</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MembershipCardPreview;
